import { Button, Text, Tooltip } from "@chakra-ui/react"
import { getMappingIterationById } from "api/mapping/iteration"
import { SMRMetadatasParams } from "component/mapping/iteration/metadatas/SMRMetadatasParams"
import { UTDMetadatasParams } from "component/mapping/iteration/metadatas/UTDMetadatasParams"
import { MappingResultsTable } from "component/mapping/result/MappingResultsTable"
import { SmrStatusFilter } from "component/mapping/result/SMRResultsFilters"
import { LoadingPage } from "component/page/LoadingPage"
import { Page } from "component/page/Page"
import { StickyBottomBar } from "component/page/StickyBottomBar"
import { notify } from "misc/notifications"
import { IterationStatus } from "constant/iterationStatus"
import { IterationType } from "constant/iterationType"
import { FC, useCallback, useEffect, useMemo, useState, useTransition } from "react"
import { FaCheckSquare } from "react-icons/fa"
import { useQuery } from "react-query"
import { useParams } from "react-router"
import {
  useMappingResultsUpdateMutation,
  useMappingResultsUploadToKafkaMutation,
} from "service/mapping/resultService"
import { IterationWithResults } from "type/mapping/iteration"
import { SmrMetadatas, UTDMetadatas } from "type/mapping/iterationMetadatas"
import {
  CorrectedResult,
  MappingResult,
  MappingResultsUpload,
  MappingResultUpdate,
} from "type/mapping/result"
import { WithId, WithStrId } from "type/withId"
import {
  getMappedNomenclature,
  isMappedNomenclatureValid,
} from "util/validation/mappedNomenclature"

type Params = {
  id: string
}

export const MappingIterationResults: FC = () => {
  const { id: iterationId } = useParams<Params>()

  const updateMutation = useMappingResultsUpdateMutation()
  const uploadToKafkaMutation = useMappingResultsUploadToKafkaMutation()

  const isMutationsLoading = updateMutation.isLoading || uploadToKafkaMutation.isLoading

  const { data: mappingIteration, isLoading } = useQuery<WithStrId<IterationWithResults>>(
    ["mappingIteration", iterationId],
    () => getMappingIterationById(iterationId!),
    {
      enabled: !!iterationId,
    },
  )
  const isIterationExists = !!mappingIteration

  const metadatas = mappingIteration?.metadatas as UTDMetadatas | undefined

  const isUTDIteration = mappingIteration?.type
    ?.toLowerCase()
    .includes(IterationType.UTD.toLowerCase())

  // Смета (СМР): своя шапка, вариант "нет в НСИ" и строки без сопоставления (KRE-713)
  const isSMRIteration = mappingIteration?.type === IterationType.SMR
  const smrMetadatas = isSMRIteration
    ? (mappingIteration?.metadatas as SmrMetadatas | undefined)
    : undefined

  const status = mappingIteration?.status
  const isIterationApproved = status === IterationStatus.APPROVED

  const results = mappingIteration?.results as WithId<MappingResult>[] | undefined
  const isResultsExist = !!results

  const prevCorrectedResults: CorrectedResult[] = useMemo(
    () =>
      results?.map(
        (result) =>
          ({
            result_id: result.corrected_nomenclature?.result_id || result.id,
            nomenclature: result.corrected_nomenclature?.nomenclature,
            feedback: result.corrected_nomenclature?.feedback,
          }) as CorrectedResult,
      ) || [],
    [results],
  )

  const [correctedResults, setCorrectedResults] =
    useState<CorrectedResult[]>(prevCorrectedResults)

  const checkedCount = correctedResults.filter((result) => !!result.nomenclature).length
  const isAllResultsCorrected = correctedResults.every((result) => !!result.nomenclature)
  const isSendBtnDisabled = !isAllResultsCorrected
  const uncheckedCount = correctedResults.length - checkedCount

  // Что "Отметить все" поставит строке: материал, найденный моделью.
  // Без сопоставления — ничего: такие строки сметчик разбирает сам
  // (выбор из НСИ или "Нет в НСИ"), а не отправляет в BI не глядя
  const getMappedChoice = useCallback(
    (mappingResult: WithId<MappingResult>) => {
      const mappedNomenclature = getMappedNomenclature(mappingResult)
      const isMapped =
        isMappedNomenclatureValid(mappingResult) &&
        !!mappedNomenclature?.nomenclature.trim() &&
        // Название без кода НСИ — выдумка LLM, а не материал из справочника
        (!isSMRIteration || !!mappedNomenclature.material_code)
      return isMapped
        ? {
            id: mappedNomenclature!.nomenclature_guid,
            name: mappedNomenclature!.nomenclature,
            material_code: mappedNomenclature!.material_code,
          }
        : undefined
    },
    [isSMRIteration],
  )

  // Сколько строк "Отметить все" ещё может отметить
  const markableCount = useMemo(() => {
    const checkedIds = new Set(
      correctedResults.filter((result) => !!result.nomenclature).map((r) => r.result_id),
    )
    return (results || []).filter(
      (result) => !checkedIds.has(result.id) && !!getMappedChoice(result),
    ).length
  }, [results, correctedResults, getMappedChoice])

  // Фильтр статуса таблицы — здесь: после "Отметить все" переключаем на
  // "Не проверены", чтобы сметчик увидел, что осталось
  const [reviewStatus, setReviewStatus] = useState<SmrStatusFilter>("all")

  // useCallback: одна и та же функция между рендерами — иначе memo у строк
  // таблицы не работает и каждая правка перерисовывает все строки
  const handleCorrectNomenclatureSelect = useCallback((result: CorrectedResult) => {
    setCorrectedResults((prevResultsList) => {
      const prevResult = prevResultsList.find(
        (prevResult) => prevResult.result_id === result.result_id,
      )

      // Save any feedback
      if (result.feedback === null && !!prevResult?.feedback) {
        result.feedback = prevResult.feedback || undefined
      }

      const filteredResultsList = prevResultsList.filter(
        (prevResult) => prevResult.result_id !== result.result_id,
      )

      return [...filteredResultsList, result]
    })
  }, [])

  // Одно обновление состояния на все строки (раньше — по одному на строку,
  // и каждый раз копировался весь список: на сотнях строк интерфейс подвисал).
  // Строки, где сметчик уже выбрал вариант, не трогаем — его правку не затираем
  // Перерисовка сотен строк идёт в transition: React делает её частями,
  // интерфейс не замирает, а на кнопке крутится индикатор
  const [isMarkingAll, startMarkAllTransition] = useTransition()

  const handleMarkAllAsCorrect = () => {
    if (!isResultsExist) {
      return
    }

    startMarkAllTransition(() =>
      setCorrectedResults((prevResultsList) => {
        const correctedById = new Map(
          prevResultsList.map((prevResult) => [prevResult.result_id, prevResult]),
        )

        results.forEach((mappingResult) => {
          const resultId = mappingResult.id
          const prevResult = correctedById.get(resultId)
          if (prevResult?.nomenclature) {
            return
          }

          const nomenclature = getMappedChoice(mappingResult)
          if (!nomenclature) {
            return
          }

          correctedById.set(resultId, {
            ...prevResult,
            result_id: resultId,
            nomenclature,
            feedback: prevResult?.feedback,
          } as CorrectedResult)
        })

        return Array.from(correctedById.values())
      }),
    )

    // Без сопоставления строки остаются — показываем только их и говорим,
    // что с ними делать
    const leftCount = uncheckedCount - markableCount
    if (leftCount > 0) {
      setReviewStatus("unchecked")
      notify(
        `Отмечено правильными: ${markableCount}. Без сопоставления осталось: ${leftCount} — ` +
          "выберите их из НСИ или нажмите \"Нет в НСИ\"",
        "warning",
        { autoClose: 8000 },
      )
    }
  }

  const handleResultsUpdate = async () => {
    const body: MappingResultUpdate = {
      iteration_id: iterationId!,
      corrected_results_list: correctedResults,
    }
    await updateMutation.mutateAsync(body)
  }

  const handleResultsUploadToKafka = async () => {
    const body: MappingResultsUpload = {
      iteration_id: iterationId!,
    }
    await uploadToKafkaMutation.mutateAsync(body)
  }

  const handleSubmit = async () => {
    await handleResultsUpdate()
    // Смета: итог в Кафку бэк отправляет сам в фоне после сохранения правок —
    // ждать не нужно, страницу можно закрыть сразу
    if (isSMRIteration) {
      return
    }
    await handleResultsUploadToKafka()
  }

  // Update corrected results
  useEffect(() => {
    setCorrectedResults(prevCorrectedResults)
  }, [prevCorrectedResults])

  return (
    <Page>
      {isLoading && <LoadingPage />}

      {/* Iteration Metadatas */}
      {!isLoading && isIterationExists && isUTDIteration && (
        <UTDMetadatasParams status={status} metadatas={metadatas} />
      )}
      {!isLoading && isIterationExists && isSMRIteration && (
        <SMRMetadatasParams status={status} metadatas={smrMetadatas} />
      )}

      {/* Mapping Results Table */}
      {!isLoading && isIterationExists && isResultsExist && (
        <MappingResultsTable
          metadatas={metadatas}
          results={results}
          correctedResults={correctedResults}
          onCorrectNomenclatureSelect={handleCorrectNomenclatureSelect}
          isIterationApproved={isIterationApproved}
          isUTDIteration={isUTDIteration}
          isSMRIteration={isSMRIteration}
          smrMetadatas={smrMetadatas}
          isLoading={isMutationsLoading}
          status={reviewStatus}
          onStatusChange={setReviewStatus}
        />
      )}

      {/* Action Btns: прилипают к низу экрана */}
      {!isLoading && isIterationExists && isResultsExist && !isIterationApproved && (
        <StickyBottomBar justifyContent="flex-end">
          {/* Сколько уже проверено — видно, почему "Отправить" ещё неактивна */}
          <Text fontSize="sm" color="gray.600" mr={2}>
            Проверено {checkedCount} из {correctedResults.length}
          </Text>

          {/* All Results Correct */}
          <Button
            colorScheme="gray"
            rightIcon={<FaCheckSquare color="green" />}
            onClick={handleMarkAllAsCorrect}
            isLoading={isLoading || isMarkingAll}
            // Неактивна, только когда отмечать нечего: строки без сопоставления
            // она не трогает, их разбирают вручную
            isDisabled={markableCount === 0 || isMutationsLoading}
          >
            Отметить все позиции правильными
          </Button>

          {/* Send Corrected Results Btn. Подсказка — почему неактивна */}
          <Tooltip
            label={
              <>
                Осталось проверить: {uncheckedCount}
                <br />
                Выберите вариант из НСИ или нажмите &quot;Нет в НСИ&quot;
              </>
            }
            isDisabled={!isSendBtnDisabled || isMutationsLoading}
            placement="top-end"
            hasArrow
            shouldWrapChildren
          >
            <Button
              colorScheme="purple"
              onClick={handleSubmit}
              isLoading={isMutationsLoading}
              isDisabled={isSendBtnDisabled}
            >
              Отправить
            </Button>
          </Tooltip>
        </StickyBottomBar>
      )}
    </Page>
  )
}
